'use client';

import { createContext, useContext } from 'react';

import type { ReactNode } from 'react';

type GapSize = 'sm' | 'md' | 'lg';
type LabelSize = 'sm' | 'md' | 'lg';

/**
 * Configuration options for declarative forms.
 * All properties are optional - undefined values will use component defaults.
 */
export interface FormConfig {
  /** Gap between form rows */
  rowGap?: GapSize;
  /** Gap between inputs within a row */
  inputGap?: GapSize;
  /** Gap between action buttons */
  actionsGap?: GapSize;
  /** Size of field labels */
  labelSize?: LabelSize;
  /** Text for submit button */
  submitButtonText?: string;
  /** Text for submit button while submitting */
  submitButtonTextPending?: string;
  /** Whether to show the reset button */
  showResetButton?: boolean;
  /** Whether to show separator above actions */
  showSeparator?: boolean;
  /** Text for reset button */
  resetButtonText?: string;
}

const FormConfigContext = createContext<FormConfig | undefined>(undefined);

interface FormConfigProviderProps {
  children: ReactNode;
  config: FormConfig;
}

/**
 * Provides form configuration defaults to all descendant DeclarativeForm components.
 * Useful for setting consistent form styling across an app or section.
 *
 * @example
 * ```tsx
 * // In a layout or provider component
 * <FormConfigProvider config={{ rowGap: 'sm', labelSize: 'sm' }}>
 *   <MyFormComponents />
 * </FormConfigProvider>
 *
 * // Forms will use the provided defaults
 * <DeclarativeForm config={formConfig} onSubmit={handleSubmit} />
 * ```
 */
export function FormConfigProvider({ children, config }: FormConfigProviderProps) {
  return <FormConfigContext.Provider value={config}>{children}</FormConfigContext.Provider>;
}

/**
 * Hook to access form configuration from context.
 * Returns undefined if no FormConfigProvider is present.
 *
 * @example
 * ```tsx
 * const config = useFormConfig();
 * const labelSize = props.labelSize ?? config?.labelSize ?? 'md';
 * ```
 */
export function useFormConfig(): FormConfig | undefined {
  return useContext(FormConfigContext);
}

/**
 * Merges explicit props with context defaults and component defaults.
 * Explicit props take priority over context, which takes priority over defaults.
 *
 * @param explicitProps - Props passed directly to the component
 * @param contextConfig - Config from FormConfigProvider context
 * @param componentDefaults - Component's default values
 */
export function mergeFormConfig<T extends FormConfig>(
  explicitProps: Partial<T>,
  contextConfig: FormConfig | undefined,
  componentDefaults: Required<T>
): Required<T> {
  const result = { ...componentDefaults } as Required<T>;

  // Apply context config (lower priority)
  if (contextConfig) {
    for (const key of Object.keys(componentDefaults) as (keyof T)[]) {
      const contextValue = contextConfig[key as keyof FormConfig];
      if (contextValue !== undefined) {
        (result as Record<keyof T, unknown>)[key] = contextValue;
      }
    }
  }

  // Apply explicit props (highest priority)
  for (const key of Object.keys(explicitProps) as (keyof T)[]) {
    const explicitValue = explicitProps[key];
    if (explicitValue !== undefined) {
      (result as Record<keyof T, unknown>)[key] = explicitValue;
    }
  }

  return result;
}
